import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteGmailExports } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GmailAuditModel } from '../../../../types';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteGmailExportsDialogProps {
  open: boolean;
  handleClose: () => void;
  modelsList: GmailAuditModel[];
  changeLogRoles: string[];
  selectedExports: string[];
  setSelectedExports: Dispatch<SetStateAction<string[]>>;
}

export default function DeleteGmailExportsDialog({
  open,
  handleClose,
  modelsList,
  changeLogRoles,
  selectedExports,
  setSelectedExports,
}: DeleteGmailExportsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const models = modelsList.filter((model) =>
    selectedExports.includes(model.id)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteGmailExports(models, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          models.length > 1
            ? 'Gmail exports deleted successfully!'
            : 'Gmail export deleted successfully!',
      });

      queryClient.setQueryData<GmailAuditModel[] | undefined>(
        [UserKeys.GmailAuditModels],
        (oldData) => {
          if (!oldData) return;
          oldData.forEach((model) => {
            if (selectedExports.includes(model.id)) {
              model.status = 'DELETED';
            }
          });
          return oldData;
        }
      );

      setSelectedExports([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          models.length > 1
            ? 'There was an issue deleting the selected gmail exports. Please refresh and try again.'
            : 'There was an issue deleting this gmail export. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.gmail_export.delete_gmail_export, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.gmailExport,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DeleteGmailExports.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        models.length > 1
          ? 'Delete Exported Mailboxes'
          : 'Delete Exported Mailbox'
      }
      dialogContent={
        <>
          <Typography>
            {models.length > 1 ? (
              <>
                Delete{' '}
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  {models.length}
                </Box>{' '}
                exports?
              </>
            ) : (
              'Delete this export?'
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: models.length > 1 ? 'Delete Exports' : 'Delete Export',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
